/**
 * /solo — SAA SOLO product landing (public, brand-isolated).
 *
 *  Streamlined entry point for families/athletes/charter partners. Tier comparison,
 *  Sample-Day CTA, and direct enroll paths for Track A ($399) and Track B ($299).
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, ShieldCheck, Users, Sparkles, Check, Trophy, Clock, MapPin,
  Zap, BookOpen, Play,
} from 'lucide-react';

const SoloLanding = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pb-24" data-testid="solo-landing">
      {/* HERO */}
      <div className="pt-24 pb-14 px-6 border-b-2 border-zinc-900" style={{ background: 'linear-gradient(to bottom, rgba(255,87,34,0.12), transparent 60%)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500">SAA SOLO · SELF-OPTIMIZED LEARNING OPPORTUNITY</div>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight leading-[0.98] mt-3 max-w-4xl">
            The Sport of <span className="text-orange-500">School</span>.
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl mt-5 max-w-2xl leading-relaxed">
            SOLO stands for <span className="text-white font-black">Self-Optimized Learning Opportunity</span>. Built for student-athletes who need total ownership over their time — SOLO meets the scholar at their exact academic level, then translates 100% state-aligned curriculum through real-world passions and interests. Study around workouts, travel, and competition. Not the other way around.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button onClick={() => navigate('/solo/enroll')}
              className="bg-orange-500 hover:bg-orange-400 text-black px-7 py-4 text-[12px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
              data-testid="hero-enroll-btn">
              Enroll Now <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/solo/preview"
              className="border-2 border-zinc-700 hover:border-white text-white px-7 py-4 text-[12px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
              data-testid="hero-preview-btn">
              <Play className="w-3.5 h-3.5" /> Try a Sample Day
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-[10px] uppercase tracking-widest text-zinc-500 font-black">
            <Bullet>180-day academic year</Bullet>
            <Bullet>CA · TX · FL standards</Bullet>
            <Bullet>Cancel anytime</Bullet>
            <Bullet>Head Coach parent portal</Bullet>
          </div>
        </div>
      </div>

      {/* WHAT SOLO STANDS FOR — Acronym Breakdown */}
      <div className="border-b-2 border-zinc-900 px-6 py-14 bg-black">
        <div className="container mx-auto max-w-6xl">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">What SOLO Stands For</div>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05] mb-8">
            Self-Optimized <span className="text-orange-500">Learning</span> Opportunity.
          </h2>
          <p className="text-zinc-400 text-sm md:text-base max-w-3xl leading-relaxed mb-10">
            Not "self-paced." Not a discount tier. <span className="text-white font-black">SOLO is an active, high-performance optimization tool</span> — a tactical entry point for athletes who need maximum schedule flexibility without giving up state-aligned academic standards.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <Pillar letter="S" word="Self-Optimized"
              body="Adaptive diagnostic recalibration via IXL and the Socratic AI Assistant. Every session tunes to your scholar's exact academic level to accelerate mastery — no ceiling, no floor." />
            <Pillar letter="L" word="Learning"
              body="Active academic + athletic IQ building. Core subjects translated into sports analytics, sports economics, and athlete business literacy — turning theory into things they can use tomorrow." />
            <Pillar letter="O" word="Opportunity"
              body="24/7 asynchronous access. Time sovereignty back for athletic travel, recruiting, training camps, and competition. Study when it fits — not when a bell rings." />
          </div>
        </div>
      </div>

      {/* TIER COMPARISON */}
      <div className="container mx-auto max-w-6xl px-6 py-14">
        <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">Choose Your Track</div>
        <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05] mb-8">
          Two Tracks. <span className="text-orange-500">One Standard.</span>
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <TrackCard
            testid="track-a-card"
            title="Track A · PRSVR School of Record"
            price="$399"
            tag="MOST SUPPORTED"
            blurb="SAA is the school of record. We handle admissions, transcripts, and state reporting (CA PSA / TX / FL equivalent). Reclassification and cumulative records included."
            perks={[
              'Full admissions & official transcripts',
              'Reclassification paperwork',
              'State reporting (CA/TX/FL)',
              'Cumulative records + graduation path',
              'Head Coach parent dashboard',
              'Access to the entire SAA ecosystem',
            ]}
            onEnroll={() => navigate('/solo/enroll?track=A')}
          />
          <TrackCard
            testid="track-b-card"
            title="Track B · Charter Partnership"
            price="$299"
            tag="MOST FLEXIBLE"
            blurb="Your charter remains school of record. SAA delivers the curriculum, tracks progress, and hands you charter-formatted CSV exports for your funding cycle."
            perks={[
              'Sports-themed curriculum + progress tracking',
              'Charter-formatted CSV exports',
              'PO / funding invoice tracking',
              'Head Coach parent dashboard',
              'Charter partnerships supported',
              'Add-ons available separately',
            ]}
            onEnroll={() => navigate('/solo/enroll?track=B')}
          />
        </div>
      </div>

      {/* THE SOLO SYSTEM */}
      <div className="border-t-2 border-zinc-900 px-6 py-14" style={{ background: 'linear-gradient(to bottom, rgba(255,255,255,0.02), transparent)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3">Inside The SOLO System</div>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05] mb-8">
            Built to <span className="text-orange-500">Advance</span>. Guardrailed to Master.
          </h2>
          <div className="grid md:grid-cols-3 gap-3">
            <Feature icon={<Zap className="w-5 h-5" />}
              title="Mastery Test-Out"
              body="Athletes who hit the 1-week cap can attempt a cumulative Mastery Test-Out. Score 85%+ and unlock the next week block. Strict 2-attempt cap keeps mastery honest." />
            <Feature icon={<Clock className="w-5 h-5" />}
              title="Pacing Guardrails"
              body="Built-in 1-week ahead cap prevents burnout. Miss twice on a test-out? Coach B recovery screen kicks in — active rest until the calendar catches up." />
            <Feature icon={<Trophy className="w-5 h-5" />}
              title="Applied Mastery"
              body="Every lesson ties an academic standard to a sports scenario. Master the play. Know when to run it. Apply the theory." />
            <Feature icon={<Users className="w-5 h-5" />}
              title="Head Coach Portal"
              body="Parents see live time-on-task vs state minimums, activity feed, attendance calendar, and flag travel absences without penalty." />
            <Feature icon={<MapPin className="w-5 h-5" />}
              title="State-Aligned"
              body="Every unit is anchored to real CA, TX, and FL standard codes. Charter families get CSV exports in the format your school of record expects." />
            <Feature icon={<BookOpen className="w-5 h-5" />}
              title="4 Core Subjects"
              body="Sports Math, Sports Literature, Sports Science, and Sports & History — Grade 7 launch curriculum. 36 units per subject, 5 lessons per unit, 180 days total." />
          </div>
        </div>
      </div>

      {/* CLOSING */}
      <div className="container mx-auto max-w-6xl px-6 py-14">
        <div className="border-2 border-orange-500 bg-orange-500/5 p-8 md:p-12 text-center">
          <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500 mb-3">Ready When You Are</div>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05]">
            180 Days. 4 Subjects. <br className="hidden md:block" /><span className="text-orange-500">One Head Coach.</span>
          </h2>
          <p className="text-zinc-300 max-w-xl mx-auto mt-4">
            Sports-themed. State-aligned. Applied Mastery. Cancel anytime.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <button onClick={() => navigate('/solo/enroll')}
              className="bg-orange-500 hover:bg-orange-400 text-black px-7 py-4 text-[12px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
              data-testid="footer-enroll-btn">
              <ShieldCheck className="w-4 h-4" /> Start Enrollment
            </button>
            <Link to="/solo/preview"
              className="border-2 border-zinc-700 hover:border-white text-white px-7 py-4 text-[12px] font-black uppercase tracking-[0.2em] flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> See a Sample Day
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Bullet = ({ children }) => (
  <span className="flex items-center gap-1.5">
    <span className="w-1 h-1 bg-orange-500 rounded-full" /> {children}
  </span>
);

const TrackCard = ({ testid, title, price, tag, blurb, perks, onEnroll }) => (
  <div className="border-2 border-zinc-800 bg-zinc-950 hover:border-orange-500 transition-all p-6 md:p-7 flex flex-col" data-testid={testid}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="text-lg md:text-xl font-black text-white leading-tight">{title}</div>
      {tag && <div className="text-[9px] font-black uppercase tracking-widest text-orange-500 border border-orange-500/60 px-2 py-1 whitespace-nowrap">{tag}</div>}
    </div>
    <div className="flex items-baseline gap-2 mb-3">
      <div className="text-5xl font-black text-orange-500">{price}</div>
      <div className="text-xs uppercase tracking-widest font-black text-zinc-500">/ month</div>
    </div>
    <p className="text-[13px] text-zinc-400 leading-relaxed mb-5">{blurb}</p>
    <ul className="space-y-1.5 mb-6 flex-1">
      {perks.map((p, i) => (
        <li key={i} className="text-[12px] text-zinc-300 flex items-start gap-2">
          <Check className="w-3.5 h-3.5 text-orange-500 flex-shrink-0 mt-0.5" /> {p}
        </li>
      ))}
    </ul>
    <button onClick={onEnroll}
      className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2"
      data-testid={`${testid}-enroll`}>
      Enroll · {price}/mo <ArrowRight className="w-3.5 h-3.5" />
    </button>
  </div>
);

const Feature = ({ icon, title, body }) => (
  <div className="border-2 border-zinc-900 bg-black p-5">
    <div className="text-orange-500 mb-3">{icon}</div>
    <div className="text-sm font-black uppercase tracking-tight text-white mb-2">{title}</div>
    <p className="text-[12px] text-zinc-400 leading-relaxed">{body}</p>
  </div>
);

const Pillar = ({ letter, word, body }) => (
  <div className="border-2 border-zinc-900 bg-zinc-950 p-6 relative overflow-hidden">
    <div className="absolute -right-4 -top-4 text-[140px] font-black text-orange-500/[0.06] leading-none pointer-events-none select-none">{letter}</div>
    <div className="relative">
      <div className="flex items-baseline gap-2 mb-2">
        <div className="text-3xl font-black text-orange-500">{letter}</div>
        <div className="text-lg font-black uppercase tracking-tight text-white">{word}</div>
      </div>
      <p className="text-[13px] text-zinc-400 leading-relaxed">{body}</p>
    </div>
  </div>
);

export default SoloLanding;
